import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/hooks/use-toast';
import { consumerApi, consumerPlanKeys } from '../api/consumer-api';

const PAYMENT_CONFIRMATION_POLL_MS = 3000;
// Stripe confirms within seconds; a review still unpaid after this was most likely abandoned.
const PAYMENT_CONFIRMATION_WINDOW_MS = 10 * 60 * 1000;

/** A review whose payment we're still waiting on, as opposed to one the consumer walked away from. */
export function isAwaitingPayment(review: ConsumerPolicyReview | undefined): boolean {
  return (
    review?.status === 'awaiting_payment' &&
    Date.now() - new Date(review.createdAt).getTime() < PAYMENT_CONFIRMATION_WINDOW_MS
  );
}

/**
 * The consumer's saved plan. Updates apply immediately and save in the background; when the last
 * save fails, the plan is reloaded from the server so it never shows choices that weren't saved.
 * With `pollWhileAwaitingPayment`, it re-checks until Stripe confirms the latest policy review.
 */
export function useConsumerPlan(
  clientId: string,
  { pollWhileAwaitingPayment = false, enabled = true } = {},
) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => consumerPlanKeys.detail(clientId), [clientId]);

  const query = useQuery({
    queryKey,
    queryFn: () => consumerApi.getPlan(clientId),
    enabled: !!clientId && enabled,
    // Only this consumer changes their plan and every change is applied to the cache first, so
    // refetching on mount could overwrite a save that is still on its way.
    staleTime: Infinity,
    refetchInterval: ({ state }) =>
      pollWhileAwaitingPayment && isAwaitingPayment(state.data?.policyReviews[0])
        ? PAYMENT_CONFIRMATION_POLL_MS
        : false,
  });

  // Saves share one scope so they reach the server in order; the latest state always lands last.
  const saveKey = useMemo(() => [...queryKey, 'save'], [queryKey]);
  const { mutate: save, isPending: isSaving } = useMutation({
    mutationKey: saveKey,
    mutationFn: (state: ConsumerPlanState) => consumerApi.savePlan(clientId, state),
    scope: { id: `consumer-plan-${clientId}` },
    onError: () => {
      // Each save carries the whole plan, so a newer save still queued also repairs this one.
      if (queryClient.isMutating({ mutationKey: saveKey }) > 1) return;
      toast({
        variant: 'destructive',
        title: 'We couldn’t save your last change',
        description: 'Please check your connection and make that choice again.',
      });
      return queryClient.invalidateQueries({ queryKey });
    },
  });

  const updatePlan = useCallback(
    (patch: Partial<ConsumerPlanState>) => {
      const current = queryClient.getQueryData<ConsumerPlanResponse>(queryKey);
      if (!current) return;

      const state = { ...current.state, ...patch };
      queryClient.setQueryData<ConsumerPlanResponse>(queryKey, { ...current, state });
      save(state);
    },
    [queryKey, queryClient, save],
  );

  return {
    plan: query.data?.state,
    applications: query.data?.applications ?? [],
    policyReviews: query.data?.policyReviews ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    updatePlan,
    isSaving,
  };
}
